import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';

interface ApiRequestOptions<T> {
	url: string;
	endpoint: string;
	apiKey: string;
	body: Record<string, any>;
	vinToken?: string;
	authService?: IAuthServiceV2;
	resourceHostId?: string;
	responseType?: 'json' | 'text';
	fallbackValue?: T;
}

export async function makeVehicleApiRequest<T>(options: ApiRequestOptions<T>): Promise<T> {
	const { url, endpoint, apiKey, body, vinToken, authService, resourceHostId, responseType = 'json', fallbackValue } = options;

	const headers: Record<string, string> = {
		'x-api-key': apiKey,
		Accept: 'application/json, text/plain, text/html, */*',
		'Content-Type': 'application/json',
	};

	if (vinToken !== undefined) {
		headers['x-vin-token'] = vinToken || '';
	}

	const AwsWafIntegration = typeof window !== 'undefined' ? window.AwsWafIntegration : undefined;
	let response: Response;

	const isAuthenticated = authService ? await authService.isAuthenticated() : false;

	if (isAuthenticated && authService && resourceHostId) {
		if (AwsWafIntegration) {
			headers['x-aws-waf-token'] = await AwsWafIntegration.getToken();
		}
		response = await authService.post(resourceHostId, endpoint, JSON.stringify(body), headers);
	} else {
		const fetchOptions = {
			method: 'POST',
			headers,
			body: JSON.stringify(body),
		};

		if (AwsWafIntegration) {
			response = await AwsWafIntegration.fetch(`${url}/${endpoint}`, fetchOptions);
		} else {
			response = await fetch(`${url}/${endpoint}`, fetchOptions);
		}
	}

	// AWS WAF challenge/CAPTCHA responses come back as 2xx (202 for challenge,
	// 405 for CAPTCHA) carrying an `x-amzn-waf-action` header rather than the
	// expected payload. Without the WAF SDK loaded (e.g. local dev) these are
	// returned to us directly. Detect and surface them explicitly so callers get
	// a clear, identifiable error instead of a confusing empty-body JSON parse
	// failure or a misleading "not found".
	const wafAction = response.headers.get('x-amzn-waf-action');
	if (wafAction) {
		if (fallbackValue !== undefined) {
			return fallbackValue;
		}
		throw new Error(`Request blocked by WAF (${wafAction})`);
	}

	if (!response.ok) {
		if (fallbackValue !== undefined) {
			return fallbackValue;
		}
		throw new Error(`Request failed with status ${response.status}`);
	}

	if (responseType === 'text') {
		return (await response.text()) as unknown as T;
	}

	return (await response.json()) as T;
}
